import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React from "react";
import { useTranslation } from "react-i18next";
import { buildAppLink } from "../../../utils/landingRedirect";

const scatteredSources = [
  { icon: "lucide:mail", key: "email", labelFallback: "Email", detailFallback: "Approvals, promises, requests" },
  { icon: "lucide:calendar-days", key: "meetings", labelFallback: "Meetings", detailFallback: "Decisions, owners, commitments" },
  { icon: "lucide:file-text", key: "files", labelFallback: "Files", detailFallback: "Contracts, decks, PDFs, research" },
  { icon: "lucide:audio-lines", key: "recordings", labelFallback: "Recordings", detailFallback: "Voice, video, lectures, field notes" },
];

const memorySteps = [
  {
    icon: "lucide:database-zap",
    key: "capture",
    titleFallback: "Capture every source",
    bodyFallback: "Bring in meetings, recordings, PDFs, email, links, docs, videos, and the places where context appears.",
  },
  {
    icon: "lucide:scan-search",
    key: "structure",
    titleFallback: "Structure the evidence",
    bodyFallback: "Extract decisions, people, tasks, citations, deadlines, and durable facts instead of storing a pile of files.",
  },
  {
    icon: "lucide:message-circle-question",
    key: "reason",
    titleFallback: "Reason with memory",
    bodyFallback: "Ask questions across connected memory and get source-backed answers that reflect what actually happened.",
  },
  {
    icon: "lucide:route",
    key: "orchestrate",
    titleFallback: "Orchestrate agents",
    bodyFallback: "Turn context into drafts, reports, tasks, briefs, approvals, or the next workflow an agent should run.",
  },
];

const useCases = [
  {
    icon: "lucide:network",
    key: "project",
    questionFallback: "What does this project know?",
    outcomeFallback: "See the timeline, decisions, owners, blockers, and source links across every captured input.",
  },
  {
    icon: "lucide:file-search",
    key: "pdfRecording",
    questionFallback: "Can this PDF or recording answer me?",
    outcomeFallback: "Ask across long documents, transcripts, videos, and notes with citations back to the source.",
  },
  {
    icon: "lucide:list-checks",
    key: "next",
    questionFallback: "What should I do next?",
    outcomeFallback: "Turn context into priorities, follow-ups, drafts, tasks, and a brief you can approve from mobile.",
  },
  {
    icon: "lucide:bot",
    key: "agent",
    questionFallback: "Which agent should act?",
    outcomeFallback: "Route the right memory into an agent that can draft, summarize, report, or start the next workflow.",
  },
];

const integrationItems = [
  { key: "email", fallback: "Email" },
  { key: "calendar", fallback: "Calendar" },
  { key: "meetings", fallback: "Meetings" },
  { key: "recordings", fallback: "Recordings" },
  { key: "pdfs", fallback: "PDFs" },
  { key: "documents", fallback: "Documents" },
  { key: "videos", fallback: "Videos" },
  { key: "chat", fallback: "Chat" },
  { key: "mobileApproval", fallback: "Mobile approval" },
  { key: "customAgents", fallback: "Custom agents" },
] as const;

const storyBulletKeys = ["rebuild", "sources", "emptyBox"] as const;
const outputChipKeys = ["answer", "action", "route"] as const;
const actionButtonKeys = ["ask", "sources", "agent"] as const;

const StoryDrivenSections: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <motion.section
        className="landing-snap-section flex min-h-screen items-center bg-[#f8fafc] py-16 md:py-24"
      >
        <div className="container mx-auto px-4">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
            >
              <span className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-cornflowerblue shadow-sm ring-1 ring-blue-100">
                <Icon icon="lucide:network" className="text-base" />
                {t("modern.workBrain.story.problemBadge", "The context problem")}
              </span>
              <h2 className="mt-6 text-[30px] md:text-[44px] font-bold leading-tight text-gray-900">
                {t("modern.workBrain.story.headline", "Your work already has the answer. It just lives everywhere.")}
              </h2>
              <p className="mt-5 text-lg leading-8 text-gray-600">
                {t("modern.workBrain.story.body", "Meetings, PDFs, recordings, chats, emails, links, and decisions are the raw material. YouWo turns them into source-backed memory that people and agents can use to answer, plan, and act.")}
              </p>

              <div className="mt-8 space-y-3">
                {storyBulletKeys.map((item) => (
                  <div key={item} className="flex gap-3 text-gray-700">
                    <Icon icon="lucide:check-circle-2" className="mt-1 text-cornflowerblue flex-shrink-0" />
                    <span>{t(`modern.workBrain.story.bullets.${item}`)}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t("modern.workBrain.story.snapshotTitle", "Context map")}</p>
                  <p className="text-xs text-gray-500">{t("modern.workBrain.story.snapshotSubtitle", "Connected context · Source-backed · Agent-ready")}</p>
                </div>
                <span className="rounded-md bg-blue-50 px-2.5 py-1 text-xs font-semibold text-cornflowerblue">
                  {t("modern.workBrain.story.snapshotStatus", "Agent-ready")}
                </span>
              </div>

              <div className="grid md:grid-cols-[0.85fr_1.15fr]">
                <div className="border-b border-gray-200 bg-gray-50 p-4 md:border-b-0 md:border-r">
                  <p className="text-xs font-bold uppercase text-gray-400">{t("modern.workBrain.story.before", "Before")}</p>
                  <h3 className="mt-2 text-lg font-bold text-gray-900">{t("modern.workBrain.story.contextScattered", "Context is scattered")}</h3>
                  <div className="mt-4 space-y-3">
                    {scatteredSources.map((source) => (
                      <div key={source.key} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50 text-cornflowerblue">
                          <Icon icon={source.icon} className="text-lg" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{t(`modern.workBrain.story.sources.${source.key}.label`, source.labelFallback)}</p>
                          <p className="text-xs text-gray-500">{t(`modern.workBrain.story.sources.${source.key}.detail`, source.detailFallback)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 md:p-5">
                  <div className="rounded-lg bg-gray-900 px-4 py-3 text-white">
                    <p className="text-xs font-semibold text-blue-200">{t("modern.workBrain.story.askLabel", "Ask across your context")}</p>
                    <p className="mt-1 text-sm">
                      {t("modern.workBrain.story.askQuestion", "What changed, what matters, and what can an agent prepare next?")}
                    </p>
                  </div>

                  <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center gap-2">
                      <Icon icon="lucide:sparkles" className="text-cornflowerblue" />
                      <p className="text-sm font-bold text-gray-900">{t("modern.workBrain.story.outputTitle", "YouWo output")}</p>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-gray-700">
                      {t("modern.workBrain.story.outputBody", "Project status, open risks, decisions, cited evidence, and agent-ready next actions are prepared from the same memory layer.")}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {outputChipKeys.map((chip) => (
                        <span key={chip} className="rounded-md bg-blue-50 px-2.5 py-1 text-xs font-semibold text-cornflowerblue">
                          {t(`modern.workBrain.story.outputChips.${chip}`)}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {actionButtonKeys.map((item) => (
                      <button
                        key={item}
                        className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:border-cornflowerblue hover:text-cornflowerblue"
                      >
                        <Icon icon="lucide:arrow-right" className="text-sm" />
                        {t(`modern.workBrain.story.actionButtons.${item}`)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      <motion.section
        className="landing-snap-section flex min-h-screen items-center bg-white py-16 md:py-24"
      >
        <div className="container mx-auto max-w-7xl px-4">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
            >
              <span className="text-sm font-bold uppercase text-cornflowerblue">{t("modern.workBrain.story.howBadge", "How context becomes action")}</span>
              <h2 className="mt-3 text-[30px] md:text-[44px] font-bold leading-tight text-gray-900">
                {t("modern.workBrain.story.howTitle", "Connect context first. Let workflows emerge from it.")}
              </h2>
              <p className="mt-5 text-lg leading-8 text-gray-600">
                {t("modern.workBrain.story.howBody", "A workflow is only powerful when the agent knows the underlying context. YouWo starts by making captured work usable, then turns it into repeatable actions under your control.")}
              </p>
            </motion.div>

            <div className="grid gap-4 sm:grid-cols-2">
              {memorySteps.map((step, index) => (
                <motion.div
                  key={step.key}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                  className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-cornflowerblue">
                    <Icon icon={step.icon} className="text-xl" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-gray-900">{t(`modern.workBrain.story.steps.${step.key}.title`, step.titleFallback)}</h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{t(`modern.workBrain.story.steps.${step.key}.body`, step.bodyFallback)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        className="landing-snap-section flex min-h-screen items-center bg-white py-16 md:py-24"
      >
        <div className="container mx-auto max-w-7xl px-4">
          <div>
            <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="text-sm font-bold uppercase text-cornflowerblue">{t("modern.workBrain.story.flexibleBadge", "Flexible intelligence")}</span>
                <h2 className="mt-3 text-[28px] md:text-[36px] font-bold text-gray-900">
                  {t("modern.workBrain.story.flexibleTitle", "Not a single automation. A layer agents can use.")}
                </h2>
              </div>
              <a
                href={buildAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-5 py-3 text-sm font-semibold text-white no-underline hover:bg-gray-800"
              >
                {t("modern.workBrain.story.primaryCta", "Build your first workflow")}
                <Icon icon="lucide:arrow-right" />
              </a>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={useCase.key}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                  className="rounded-lg border border-gray-200 bg-[#fbfdff] p-5"
                >
                  <Icon icon={useCase.icon} className="text-2xl text-cornflowerblue" />
                  <h3 className="mt-4 text-base font-bold text-gray-900">{t(`modern.workBrain.story.useCases.${useCase.key}.question`, useCase.questionFallback)}</h3>
                  <p className="mt-3 text-sm leading-6 text-gray-600">{t(`modern.workBrain.story.useCases.${useCase.key}.outcome`, useCase.outcomeFallback)}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mt-16 rounded-lg border border-gray-200 bg-gray-50 p-5 md:p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{t("modern.workBrain.story.noteTitle", "The email workflow is just one visible story.")}</h3>
                <p className="mt-2 text-sm text-gray-600">
                  {t("modern.workBrain.story.noteBody", "The broader brand is a memory-to-action layer: capture information, structure memory, answer with evidence, and let agents act under your control.")}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {integrationItems.map((item) => (
                  <span key={item.key} className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700">
                    {t(`modern.workBrain.story.integrations.${item.key}`, item.fallback)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default StoryDrivenSections;
